import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

const statusOptions = [
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
];

const fmtSize = (bytes) => {
  const b = Number(bytes) || 0;
  if (b > 1048576) return `${(b / 1048576).toFixed(1)} MB`;
  if (b > 1024) return `${Math.round(b / 1024)} KB`;
  return `${b} B`;
};

// UK-friendly WhatsApp link: 07123... -> wa.me/447123...
const waLink = (phone) => {
  const digits = (phone || '').replace(/\D/g, '');
  if (!digits) return null;
  const intl = digits.startsWith('0') ? `44${digits.slice(1)}` : digits;
  return `https://wa.me/${intl}`;
};

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [uploadingTo, setUploadingTo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState({ name: '', email: '', phone: '' });
  const [qs, setQs] = useState(null);
  const [qsStarting, setQsStarting] = useState(false);
  const [supplierEmail, setSupplierEmail] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [supplierMsg, setSupplierMsg] = useState('');
  const [enquiryMode, setEnquiryMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [enquiries, setEnquiries] = useState([]);
  const [sendingEnquiry, setSendingEnquiry] = useState(false);
  const [rams, setRams] = useState(null);
  const [ramsStarting, setRamsStarting] = useState(false);
  const [schedule, setSchedule] = useState([]);
  const [schedTab, setSchedTab] = useState('internal');
  const [schedModal, setSchedModal] = useState(null); // null | 'new' | item being edited
  const [schedForm, setSchedForm] = useState({ title: '', details: '', start_date: '', end_date: '', status: 'upcoming', date_unknown: false, responsible_user_id: '' });
  const [schedSaving, setSchedSaving] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const fileInput = useRef(null);
  const pendingReq = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/jobs/${id}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Job not found.' : 'Could not load job.');
    }
  }, [id]);

  const loadMessages = useCallback(async () => {
    try {
      const res = await api.get(`/api/jobs/${id}/messages`);
      setMessages(res.data);
    } catch (err) { /* thread stays empty */ }
  }, [id]);

  const loadQs = useCallback(async () => {
    try {
      const res = await api.get(`/api/jobs/${id}/qs`);
      setQs(res.data);
    } catch (err) { /* card shows the run button */ }
  }, [id]);

  const loadRams = useCallback(async () => {
    try {
      const res = await api.get(`/api/jobs/${id}/rams`);
      setRams(res.data);
    } catch (err) { /* card shows the button */ }
  }, [id]);

  const loadSchedule = useCallback(async () => {
    try {
      const res = await api.get(`/api/jobs/${id}/schedule`);
      setSchedule(res.data);
    } catch (err) { /* card shows empty state */ }
  }, [id]);

  useEffect(() => { load(); loadMessages(); loadQs(); loadRams(); loadSchedule(); }, [load, loadMessages, loadQs, loadRams, loadSchedule]);

  // People with login access — the pool for schedule responsibility
  useEffect(() => {
    api.get('/api/users').then(r => setStaffList(r.data.filter(u => u.active))).catch(() => {});
  }, []);

  // Poll while an analysis is running (they take a few minutes)
  useEffect(() => {
    if (qs?.status !== 'processing') return;
    const t = setInterval(loadQs, 10000);
    return () => clearInterval(t);
  }, [qs, loadQs]);

  useEffect(() => {
    if (rams?.status !== 'processing') return;
    const t = setInterval(() => { loadRams(); }, 10000);
    return () => clearInterval(t);
  }, [rams, loadRams]);

  // When RAMS completes, refresh the file lists so the new PDFs appear
  const ramsStatus = rams?.status;
  useEffect(() => {
    if (ramsStatus === 'complete') load();
  }, [ramsStatus, load]);

  const runQs = async () => {
    setQsStarting(true);
    setError('');
    try {
      const res = await api.post(`/api/jobs/${id}/qs`);
      setQs(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start the analysis.');
    } finally {
      setQsStarting(false);
    }
  };

  const runRams = async () => {
    setRamsStarting(true);
    setError('');
    try {
      const res = await api.post(`/api/jobs/${id}/rams`);
      setRams(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start RAMS generation.');
    } finally {
      setRamsStarting(false);
    }
  };

  const loadEnquiries = useCallback(async (reportId) => {
    try {
      const res = await api.get(`/api/jobs/${id}/qs/${reportId}/enquiries`);
      setEnquiries(res.data);
    } catch (err) { /* list stays empty */ }
  }, [id]);

  useEffect(() => {
    if (qs?.id && qs.status === 'complete') loadEnquiries(qs.id);
  }, [qs?.id, qs?.status, loadEnquiries]);

  const toggleSelect = (index) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index); else next.add(index);
      return next;
    });
  };

  const toggleSelectCategory = (category) => {
    const idxs = qs.result.shopping_list
      .map((item, i) => (item.category === category ? i : -1))
      .filter(i => i >= 0);
    setSelected(prev => {
      const next = new Set(prev);
      const allIn = idxs.every(i => next.has(i));
      idxs.forEach(i => allIn ? next.delete(i) : next.add(i));
      return next;
    });
  };

  const sendEnquiry = async (e) => {
    e.preventDefault();
    if (!selected.size || sendingEnquiry) return;
    setSendingEnquiry(true);
    setSupplierMsg('');
    setError('');
    try {
      await api.post(`/api/jobs/${id}/qs/${qs.id}/enquiries`, {
        supplier_email: supplierEmail,
        supplier_name: supplierName.trim() || undefined,
        indexes: [...selected].sort((a, b) => a - b)
      });
      setSupplierMsg(`Pricing request (${selected.size} lines) sent to ${supplierEmail}`);
      setEnquiryMode(false);
      setSelected(new Set());
      setSupplierEmail('');
      setSupplierName('');
      loadEnquiries(qs.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send the enquiry.');
    } finally {
      setSendingEnquiry(false);
    }
  };

  const toggleEnquiryStatus = async (enq) => {
    const status = enq.status === 'replied' ? 'sent' : 'replied';
    try {
      await api.patch(`/api/jobs/${id}/qs/enquiries/${enq.id}`, { status });
      loadEnquiries(qs.id);
    } catch (err) {
      setError('Could not update the enquiry.');
    }
  };

  const toggleCheck = async (index) => {
    const current = !!qs.result.shopping_list[index].checked;
    // Optimistic update — flip locally, then persist
    setQs(prev => {
      const next = structuredClone(prev);
      next.result.shopping_list[index].checked = !current;
      return next;
    });
    try {
      await api.patch(`/api/jobs/${id}/qs/${qs.id}/check`, { index, checked: !current });
    } catch (err) {
      loadQs(); // revert to server truth
    }
  };

  const addCustomer = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api.post(`/api/jobs/${id}/customers`, customerForm);
      setShowAddCustomer(false);
      setCustomerForm({ name: '', email: '', phone: '' });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add customer.');
    }
  };

  const removeCustomer = async (c) => {
    if (!window.confirm(`Remove ${c.name} from this job? Their portal login stays but they lose access to this job.`)) return;
    await api.delete(`/api/jobs/${id}/customers/${c.id}`);
    load();
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/api/jobs/${id}/messages`, { body: message });
      setMessage('');
      loadMessages();
    } catch (err) {
      setError('Message failed to send.');
    } finally {
      setSending(false);
    }
  };

  const pickFiles = (requirementId) => {
    pendingReq.current = requirementId;
    fileInput.current.click();
  };

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || !pendingReq.current) return;
    setUploadingTo(pendingReq.current);
    setError('');
    try {
      const form = new FormData();
      form.append('requirement_id', pendingReq.current);
      files.forEach(f => form.append('files', f));
      await api.post(`/api/jobs/${id}/files`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed.');
    } finally {
      setUploadingTo(null);
      pendingReq.current = null;
    }
  };

  const download = async (file) => {
    try {
      const res = await api.get(`/api/jobs/${id}/files/${file.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Download failed.');
    }
  };

  const removeFile = async (file) => {
    if (!window.confirm(`Delete "${file.filename}"?`)) return;
    await api.delete(`/api/jobs/${id}/files/${file.id}`);
    load();
  };

  const setStatus = async (status) => {
    await api.patch(`/api/jobs/${id}`, { status });
    load();
  };

  const openSchedItem = (item) => {
    if (item) {
      setSchedModal(item);
      setSchedForm({
        title: item.title,
        details: item.details || '',
        start_date: item.start_date ? item.start_date.substring(0, 10) : '',
        end_date: item.end_date ? item.end_date.substring(0, 10) : '',
        status: item.status,
        date_unknown: !item.start_date && !item.end_date,
        responsible_user_id: item.responsible_user_id || ''
      });
    } else {
      setSchedModal('new');
      setSchedForm({ title: '', details: '', start_date: '', end_date: '', status: 'upcoming', date_unknown: false, responsible_user_id: '' });
    }
    setError('');
  };

  const saveSchedItem = async (e) => {
    e.preventDefault();
    if (schedSaving) return;
    setSchedSaving(true);
    setError('');
    try {
      const payload = {
        title: schedForm.title,
        details: schedForm.details || null,
        start_date: schedForm.date_unknown ? null : (schedForm.start_date || null),
        end_date: schedForm.date_unknown ? null : (schedForm.end_date || null),
        status: schedForm.status,
        date_unknown: schedForm.date_unknown,
        responsible_user_id: schedForm.responsible_user_id ? Number(schedForm.responsible_user_id) : null,
        clear_responsible: !schedForm.responsible_user_id
      };
      if (schedModal === 'new') {
        await api.post(`/api/jobs/${id}/schedule`, { ...payload, audience: schedTab });
      } else {
        await api.patch(`/api/jobs/${id}/schedule/${schedModal.id}`, payload);
      }
      setSchedModal(null);
      loadSchedule();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the schedule item.');
    } finally {
      setSchedSaving(false);
    }
  };

  const setSchedStatus = async (item, status) => {
    try {
      await api.patch(`/api/jobs/${id}/schedule/${item.id}`, { status });
      loadSchedule();
    } catch (err) {
      setError('Could not update the item.');
    }
  };

  const removeSchedItem = async (item) => {
    if (!window.confirm(`Remove "${item.title}" from the ${schedTab === 'client' ? 'client-facing' : 'internal'} schedule?`)) return;
    await api.delete(`/api/jobs/${id}/schedule/${item.id}`);
    loadSchedule();
  };

  const copyToClient = async () => {
    if (!window.confirm('Copy the internal schedule to the client-facing version? This REPLACES the current client-facing schedule.')) return;
    try {
      await api.post(`/api/jobs/${id}/schedule/copy-to-client`);
      setSchedTab('client');
      loadSchedule();
    } catch (err) {
      setError('Could not copy the schedule.');
    }
  };

  const toggleArchive = async () => {
    const archiving = !data.job.archived;
    if (archiving && !window.confirm(`Archive "${data.job.name}"? It disappears from the jobs board (find it under the Archived view). Nothing is deleted.`)) return;
    try {
      await api.patch(`/api/jobs/${id}/archive`, { archived: archiving });
      if (archiving) navigate('/jobs');
      else load();
    } catch (err) {
      setError('Could not update the job.');
    }
  };

  const deleteJob = async () => {
    const typed = window.prompt(`This PERMANENTLY deletes "${data.job.name}" — all files, messages, QS and RAMS reports, and the schedule. It cannot be undone.\n\nType the job name to confirm:`);
    if (typed === null) return;
    if (typed.trim() !== data.job.name) return setError('Job name did not match — nothing was deleted.');
    try {
      await api.delete(`/api/jobs/${id}`);
      navigate('/jobs');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not delete the job.');
    }
  };

  if (error && !data) return <div className="alert alert-error">{error}</div>;
  if (!data) return <div className="loader" />;

  const { job, requirements, files, customers = [] } = data;
  const filesByReq = {};
  files.forEach(f => {
    filesByReq[f.requirement_id] = filesByReq[f.requirement_id] || [];
    filesByReq[f.requirement_id].push(f);
  });
  const requiredMissing = requirements.filter(r => r.required && !(filesByReq[r.id] || []).length);

  return (
    <div>
      <input type="file" ref={fileInput} multiple style={{ display: 'none' }} onChange={uploadFiles} />

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/jobs')} style={{ marginBottom: 12 }}>← All Jobs</button>
          <h1 className="page-title">{job.name}</h1>
          <p className="page-subtitle">
            {[job.client_name, job.site_address].filter(Boolean).join(' · ') || 'No client details yet'}
          </p>
        </div>
        <select className="form-control" style={{ width: 'auto' }} value={job.status} onChange={e => setStatus(e.target.value)}>
          {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {requiredMissing.length > 0 ? (
        <div className="alert alert-info">
          <strong>{requiredMissing.length} compulsory document type{requiredMissing.length === 1 ? '' : 's'} still missing:</strong>{' '}
          {requiredMissing.map(r => r.label).join(', ')}
        </div>
      ) : (
        <div className="alert alert-success">All compulsory documents are in place for this job.</div>
      )}

      {/* Schedule of works — internal + client-facing versions */}
      {(() => {
        const items = schedule.filter(s => s.audience === schedTab);
        const schedStatusMeta = {
          upcoming: { label: 'Upcoming', className: 'badge badge-holiday' },
          in_progress: { label: 'In Progress', className: 'badge badge-approved' },
          complete: { label: 'Complete', className: 'badge badge-approved' },
          delayed: { label: 'Delayed', className: 'badge badge-pending' },
        };
        const fmtD = (d) => d ? new Date(d.substring(0, 10) + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : null;
        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2 className="card-title">Schedule of Works</h2>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {schedTab === 'internal' && items.length > 0 && (
                  <button className="btn btn-outline btn-sm" onClick={copyToClient}>Copy to client version</button>
                )}
                <button className="btn btn-gold btn-sm" onClick={() => openSchedItem(null)}>+ Add Item</button>
              </div>
            </div>
            <div className="card-body">
              <div style={{ display: 'flex', gap: 4, marginBottom: 14, background: 'var(--grey-light)', border: '1px solid var(--border)', borderRadius: 6, padding: 4, width: 'fit-content' }}>
                {[['internal', 'Internal'], ['client', 'Client-Facing']].map(([v, label]) => (
                  <button key={v} onClick={() => setSchedTab(v)} style={{
                    padding: '6px 14px', border: 'none', borderRadius: 4, cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 500,
                    background: schedTab === v ? 'var(--navy)' : 'transparent',
                    color: schedTab === v ? 'var(--white)' : 'var(--grey-mid)',
                    transition: 'all 0.15s'
                  }}>{label}</button>
                ))}
              </div>
              {schedTab === 'client' && (
                <p style={{ fontSize: 12, color: 'var(--grey-mid)', marginBottom: 12 }}>
                  This version is what customers see on their portal. The internal version never leaves the office.
                </p>
              )}
              {items.length === 0 ? (
                <div className="empty-state" style={{ padding: '20px 0' }}>
                  {schedTab === 'client'
                    ? 'No client-facing schedule yet. Add items, or build the internal version first and copy it across.'
                    : 'No schedule yet. Add the first phase of works.'}
                </div>
              ) : items.map((item, i) => {
                const meta = schedStatusMeta[item.status] || schedStatusMeta.upcoming;
                const dates = [fmtD(item.start_date), fmtD(item.end_date)].filter(Boolean);
                return (
                  <div key={item.id} style={{ display: 'flex', gap: 12, padding: '10px 4px', borderBottom: '1px solid var(--border)', alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 26, height: 26, borderRadius: '50%', background: item.status === 'complete' ? 'var(--gold)' : 'var(--grey-light)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: item.status === 'complete' ? 'var(--white)' : 'var(--grey-mid)', fontWeight: 600 }}>
                      {item.status === 'complete' ? '✓' : i + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ color: 'var(--navy)', fontSize: 14, fontWeight: 500 }}>{item.title}</span>
                        <span className={meta.className}>{meta.label}</span>
                      </div>
                      <div style={{ fontSize: 12, color: dates.length > 0 ? 'var(--gold-dark)' : 'var(--grey-mid)', marginTop: 3, fontStyle: dates.length > 0 ? 'normal' : 'italic' }}>
                        {dates.length > 0 ? dates.join(' — ') : 'Dates to be confirmed'}
                      </div>
                      {item.details && (
                        <div style={{ fontSize: 12, color: 'var(--grey-mid)', marginTop: 3, whiteSpace: 'pre-wrap' }}>{item.details}</div>
                      )}
                      <div style={{ fontSize: 11, color: 'var(--grey-mid)', marginTop: 4 }}>
                        {item.responsible_name
                          ? <>Responsibility: <strong style={{ color: 'var(--navy)' }}>{item.responsible_name}</strong></>
                          : 'No one assigned'}
                        {item.created_by_name && <> · Added by {item.created_by_name}</>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <select className="form-control" style={{ width: 'auto', padding: '4px 8px', fontSize: 12 }} value={item.status} onChange={e => setSchedStatus(item, e.target.value)}>
                        <option value="upcoming">Upcoming</option>
                        <option value="in_progress">In Progress</option>
                        <option value="complete">Complete</option>
                        <option value="delayed">Delayed</option>
                      </select>
                      <button className="btn btn-outline btn-sm" onClick={() => openSchedItem(item)}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => removeSchedItem(item)}>Remove</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* RAMS generation */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <h2 className="card-title">Site Documents <span className="badge badge-holiday" style={{ marginLeft: 8 }}>AI</span></h2>
          <button
            className="btn btn-gold btn-sm"
            disabled={ramsStarting || rams?.status === 'processing'}
            onClick={runRams}
          >
            {rams?.status === 'processing' ? 'Generating…' : 'Generate Method Statement & Risk Assessment'}
          </button>
        </div>
        <div className="card-body">
          {!rams && (
            <div style={{ color: 'var(--grey-mid)', fontSize: 13 }}>
              Drafts a site-specific method statement and risk assessment from the uploaded drawings, and files them into the document categories above. Generated documents are <strong>drafts</strong> — review and sign before issuing to site.
            </div>
          )}
          {rams?.status === 'processing' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div className="loader" style={{ margin: 0 }} />
              <div style={{ color: 'var(--grey-mid)', fontSize: 14 }}>
                Reading the drawings and drafting the documents… a few minutes. They'll appear in the Method Statements and Risk Assessments categories above.
              </div>
            </div>
          )}
          {rams?.status === 'failed' && (
            <div className="alert alert-error" style={{ marginBottom: 0 }}>Generation failed: {rams.error}</div>
          )}
          {rams?.status === 'complete' && (
            <>
              <div className="alert alert-success" style={{ fontSize: 13 }}>
                Draft documents generated ({rams.result?.phases} method phases, {rams.result?.hazards} assessed hazards) and filed under Method Statements and Risk Assessments above. <strong>Review, amend and sign before issuing to site.</strong>
              </div>
              {rams.result?.review_notes?.length > 0 && (
                <div style={{ color: '#555', fontSize: 13, lineHeight: 1.7 }}>
                  <strong style={{ color: 'var(--navy)' }}>Confirm before signing:</strong>
                  <ul style={{ margin: '4px 0 0 18px' }}>
                    {rams.result.review_notes.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                </div>
              )}
              <p style={{ color: '#a9a9a9', fontSize: 11, marginTop: 10, marginBottom: 0 }}>
                Generated {new Date(rams.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} by {rams.created_by_name || 'a director'}. Re-generate any time — new drafts are added alongside the old ones.
              </p>
            </>
          )}
        </div>
      </div>

      {/* QS analysis */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <h2 className="card-title">QS Analysis <span className="badge badge-holiday" style={{ marginLeft: 8 }}>AI</span></h2>
          <button
            className="btn btn-gold btn-sm"
            disabled={qsStarting || qs?.status === 'processing'}
            onClick={runQs}
          >
            {qs?.status === 'processing' ? 'Analysing…' : qs ? 'Re-run Analysis' : 'Run QS Analysis'}
          </button>
        </div>
        <div className="card-body">
          {!qs && (
            <div className="empty-state">
              Upload architect drawings above, then run the analysis — it measures the drawings and builds a materials shopping list ready to price.
            </div>
          )}

          {qs?.status === 'processing' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div className="loader" style={{ margin: 0 }} />
              <div style={{ color: '#666', fontSize: 14 }}>
                Reading the drawings and measuring up… this takes a few minutes. You can leave this page — the result will be here when you come back.
              </div>
            </div>
          )}

          {qs?.status === 'failed' && (
            <div className="alert alert-error" style={{ marginBottom: 0 }}>
              Analysis failed: {qs.error}
            </div>
          )}

          {qs?.status === 'complete' && qs.result && (
            <>
              <p style={{ color: '#555', fontSize: 14, lineHeight: 1.6 }}>{qs.result.summary}</p>

              {qs.result.warnings?.length > 0 && (
                <div className="alert alert-error" style={{ fontSize: 13 }}>
                  <strong>Check before ordering:</strong>
                  <ul style={{ margin: '6px 0 0 18px' }}>
                    {qs.result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '20px 0 10px' }}>
                <h3 style={{ color: 'var(--gold-dark)', fontSize: 13, letterSpacing: 1.5, textTransform: 'uppercase', margin: 0 }}>Shopping List</h3>
                <span style={{ color: 'var(--grey-mid)', fontSize: 12 }}>
                  {qs.result.shopping_list?.filter(i => i.checked).length || 0} of {qs.result.shopping_list?.length || 0} checked
                </span>
              </div>
              <div className="table-wrap">
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {['✓', 'Category', 'Material', 'Specification', 'Qty'].map(h => (
                        <th key={h} style={{ textAlign: h === 'Qty' ? 'right' : 'left', padding: '8px 10px', color: 'var(--gold-dark)', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {qs.result.shopping_list?.map((item, i) => {
                      const done = !!item.checked;
                      const isSelected = enquiryMode && selected.has(i);
                      const strike = done && !enquiryMode ? { textDecoration: 'line-through', opacity: 0.55 } : {};
                      return (
                        <tr
                          key={i}
                          onClick={() => enquiryMode ? toggleSelect(i) : toggleCheck(i)}
                          style={{ cursor: 'pointer', background: isSelected ? '#faf3e8' : undefined }}
                          title={enquiryMode ? 'Click to add/remove from this enquiry' : (done ? 'Click to un-check' : 'Click to check off')}
                        >
                          <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', width: 34 }}>
                            <input
                              type="checkbox"
                              checked={enquiryMode ? isSelected : done}
                              readOnly
                              style={{ width: 17, height: 17, accentColor: 'var(--gold-dark)', cursor: 'pointer', pointerEvents: 'none' }}
                            />
                          </td>
                          <td
                            onClick={enquiryMode ? (e) => { e.stopPropagation(); toggleSelectCategory(item.category); } : undefined}
                            title={enquiryMode ? 'Click to select/deselect this whole section' : undefined}
                            style={{ padding: '8px 10px', color: 'var(--gold-dark)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)', textDecoration: enquiryMode ? 'underline dotted' : undefined, ...strike }}
                          >
                            {item.category}
                          </td>
                          <td style={{ padding: '8px 10px', color: 'var(--navy)', borderBottom: '1px solid var(--border)', ...strike }}>{item.material}</td>
                          <td style={{ padding: '8px 10px', color: 'var(--grey-mid)', borderBottom: '1px solid var(--border)', ...strike }}>{item.specification}{item.notes ? ` — ${item.notes}` : ''}</td>
                          <td style={{ padding: '8px 10px', color: 'var(--navy)', textAlign: 'right', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)', ...strike }}>{item.quantity} {item.unit}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {(qs.result.building_regs_notes?.length > 0 || qs.result.assumptions?.length > 0) && (
                <details style={{ marginTop: 16 }}>
                  <summary style={{ color: 'var(--gold-dark)', fontSize: 13, cursor: 'pointer' }}>
                    Measurements, building regs notes &amp; assumptions
                  </summary>
                  <div style={{ marginTop: 12, color: '#666', fontSize: 13, lineHeight: 1.7 }}>
                    {qs.result.takeoff?.length > 0 && (
                      <>
                        <strong style={{ color: 'var(--navy)' }}>Take-off:</strong>
                        <ul style={{ margin: '4px 0 12px 18px' }}>
                          {qs.result.takeoff.map((t, i) => (
                            <li key={i}>{t.element} ({t.location}): {t.quantity} {t.unit} — {t.basis}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    {qs.result.building_regs_notes?.length > 0 && (
                      <>
                        <strong style={{ color: 'var(--navy)' }}>Building regs:</strong>
                        <ul style={{ margin: '4px 0 12px 18px' }}>
                          {qs.result.building_regs_notes.map((n, i) => <li key={i}>{n}</li>)}
                        </ul>
                      </>
                    )}
                    {qs.result.assumptions?.length > 0 && (
                      <>
                        <strong style={{ color: 'var(--navy)' }}>Assumptions:</strong>
                        <ul style={{ margin: '4px 0 0 18px' }}>
                          {qs.result.assumptions.map((a, i) => <li key={i}>{a}</li>)}
                        </ul>
                      </>
                    )}
                  </div>
                </details>
              )}

              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                {!enquiryMode ? (
                  <button className="btn btn-gold" onClick={() => { setEnquiryMode(true); setSelected(new Set()); setSupplierMsg(''); }}>
                    + New Supplier Enquiry
                  </button>
                ) : (
                  <form onSubmit={sendEnquiry}>
                    <div className="alert alert-info" style={{ fontSize: 13 }}>
                      <strong>{selected.size} line{selected.size === 1 ? '' : 's'} selected.</strong>{' '}
                      Click lines above to select them, or click a <u style={{ textDecorationStyle: 'dotted' }}>section name</u> to grab the whole section — then choose the supplier below.
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <input
                        className="form-control"
                        style={{ flex: '1 1 160px' }}
                        placeholder="Supplier name (optional)"
                        value={supplierName}
                        onChange={e => setSupplierName(e.target.value)}
                      />
                      <input
                        className="form-control"
                        style={{ flex: '1 1 220px' }}
                        type="email"
                        placeholder="Supplier email address"
                        value={supplierEmail}
                        onChange={e => setSupplierEmail(e.target.value)}
                        required
                      />
                      <button type="submit" className="btn btn-gold" disabled={!selected.size || sendingEnquiry}>
                        {sendingEnquiry ? 'Sending…' : `Send ${selected.size || ''} Line${selected.size === 1 ? '' : 's'} for Pricing`}
                      </button>
                      <button type="button" className="btn btn-outline" onClick={() => { setEnquiryMode(false); setSelected(new Set()); }}>Cancel</button>
                    </div>
                  </form>
                )}
                {supplierMsg && <div className="alert alert-success" style={{ marginTop: 12, marginBottom: 0 }}>{supplierMsg}</div>}
              </div>

              {enquiries.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <h3 style={{ color: 'var(--gold-dark)', fontSize: 13, letterSpacing: 1.5, textTransform: 'uppercase', margin: '0 0 10px' }}>Supplier Enquiries</h3>
                  {enquiries.map(enq => {
                    const sections = [...new Set((enq.line_indexes || [])
                      .map(i => qs.result.shopping_list?.[i]?.category)
                      .filter(Boolean))];
                    const replied = enq.status === 'replied';
                    return (
                      <div key={enq.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 4px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <div style={{ color: 'var(--navy)', fontSize: 14 }}>
                            {enq.supplier_name ? `${enq.supplier_name} — ` : ''}{enq.supplier_email}
                          </div>
                          <div style={{ color: 'var(--grey-mid)', fontSize: 12 }}>
                            {enq.line_indexes?.length} line{enq.line_indexes?.length === 1 ? '' : 's'}{sections.length ? ` · ${sections.join(', ')}` : ''}
                          </div>
                          <div style={{ color: '#a9a9a9', fontSize: 11 }}>
                            Sent {new Date(enq.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}{enq.sent_by_name ? ` by ${enq.sent_by_name}` : ''}
                            {replied && enq.replied_at ? ` · replied ${new Date(enq.replied_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
                          </div>
                        </div>
                        <span className={`badge ${replied ? 'badge-approved' : 'badge-pending'}`}>
                          {replied ? 'Replied' : 'Awaiting reply'}
                        </span>
                        <button className="btn btn-outline btn-sm" onClick={() => toggleEnquiryStatus(enq)}>
                          {replied ? 'Mark awaiting' : 'Mark replied'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <p style={{ color: '#a9a9a9', fontSize: 11, marginTop: 12, marginBottom: 0 }}>
                AI-generated estimate from the uploaded drawings — review quantities and assumptions before ordering. Run by {qs.created_by_name || 'a director'}, {new Date(qs.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Customers on this job */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="card-title">Customers</h2>
          <button className="btn btn-gold btn-sm" onClick={() => setShowAddCustomer(s => !s)}>
            {showAddCustomer ? 'Cancel' : '+ Add Customer'}
          </button>
        </div>
        <div className="card-body">
          {showAddCustomer && (
            <form onSubmit={addCustomer} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <input className="form-control" style={{ flex: '1 1 150px' }} placeholder="Name" value={customerForm.name} onChange={e => setCustomerForm({ ...customerForm, name: e.target.value })} required />
              <input className="form-control" style={{ flex: '1 1 180px' }} type="email" placeholder="Email" value={customerForm.email} onChange={e => setCustomerForm({ ...customerForm, email: e.target.value })} required />
              <input className="form-control" style={{ flex: '1 1 130px' }} placeholder="Mobile (for WhatsApp)" value={customerForm.phone} onChange={e => setCustomerForm({ ...customerForm, phone: e.target.value })} />
              <button type="submit" className="btn btn-gold">Invite</button>
            </form>
          )}
          {customers.length === 0 ? (
            <div className="empty-state">No customers linked yet. Add one and they'll get a portal invite by email.</div>
          ) : customers.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 4px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ color: 'var(--navy)', fontSize: 14 }}>{c.name}</div>
                <div style={{ color: 'var(--grey-mid)', fontSize: 12 }}>{c.email}{c.phone ? ` · ${c.phone}` : ''}</div>
              </div>
              <a className="btn btn-outline btn-sm" href={`mailto:${c.email}`}>Email</a>
              {waLink(c.phone) && (
                <a className="btn btn-outline btn-sm" href={waLink(c.phone)} target="_blank" rel="noreferrer" style={{ color: 'var(--success)', borderColor: 'rgba(159,214,162,0.4)' }}>
                  WhatsApp
                </a>
              )}
              <button className="btn btn-danger btn-sm" onClick={() => removeCustomer(c)}>Remove</button>
            </div>
          ))}
        </div>
      </div>

      {/* Message thread — visible to both directors and the customer */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h2 className="card-title">Customer Messages</h2></div>
        <div className="card-body">
          <div style={{ maxHeight: 360, overflowY: 'auto', marginBottom: 16, paddingRight: 6 }}>
            {messages.length === 0 && <div className="empty-state">No messages yet. Anything you send here emails the customer and shows in their portal.</div>}
            {messages.map(m => {
              const staff = m.sender_type === 'staff';
              return (
                <div key={m.id} style={{ display: 'flex', justifyContent: staff ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
                  <div style={{
                    maxWidth: '78%', padding: '10px 14px', borderRadius: 8,
                    background: staff ? '#faf3e8' : 'var(--grey-light)',
                    border: `1px solid ${staff ? '#eadbc3' : 'var(--border)'}`
                  }}>
                    <div style={{ color: 'var(--gold-dark)', fontSize: 11, marginBottom: 4 }}>
                      {staff ? (m.staff_name || 'Crossman & Walsh') : (m.customer_name || 'Customer')}
                    </div>
                    <div style={{ color: 'var(--navy)', fontSize: 14, whiteSpace: 'pre-wrap' }}>{m.body}</div>
                    <div style={{ color: '#a9a9a9', fontSize: 10, marginTop: 6 }}>
                      {new Date(m.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <form onSubmit={sendMessage} style={{ display: 'flex', gap: 10 }}>
            <input className="form-control" style={{ flex: 1 }} placeholder="Message the customer..." value={message} onChange={e => setMessage(e.target.value)} />
            <button type="submit" className="btn btn-gold" disabled={sending}>{sending ? 'Sending...' : 'Send'}</button>
          </form>
        </div>
      </div>

      {requirements.map(r => {
        const reqFiles = filesByReq[r.id] || [];
        const missing = r.required && !reqFiles.length;
        return (
          <div className="card" key={r.id} style={{ marginBottom: 16, borderColor: missing ? 'var(--warning)' : undefined }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {r.label}
                {r.required && (
                  <span className={`badge ${missing ? 'badge-pending' : 'badge-approved'}`}>
                    {missing ? 'Required — missing' : 'Required ✓'}
                  </span>
                )}
              </h2>
              <button
                className="btn btn-gold btn-sm"
                disabled={uploadingTo === r.id}
                onClick={() => pickFiles(r.id)}
              >
                {uploadingTo === r.id ? 'Uploading...' : '+ Upload'}
              </button>
            </div>
            {reqFiles.length > 0 && (
              <div className="card-body">
                {reqFiles.map(f => (
                  <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 4px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: 'var(--navy)', fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.filename}</div>
                      <div style={{ color: '#a9a9a9', fontSize: 11 }}>
                        {fmtSize(f.size_bytes)} · {f.uploaded_by_name || 'unknown'} · {new Date(f.created_at).toLocaleDateString('en-GB')}
                      </div>
                    </div>
                    <button className="btn btn-outline btn-sm" onClick={() => download(f)}>Download</button>
                    <button className="btn btn-danger btn-sm" onClick={() => removeFile(f)}>Delete</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {job.notes && (
        <div className="card">
          <div className="card-header"><h2 className="card-title">Notes</h2></div>
          <div className="card-body" style={{ color: '#555', fontSize: 14, whiteSpace: 'pre-wrap' }}>{job.notes}</div>
        </div>
      )}

      {/* Archive / delete */}
      <div className="card" style={{ marginTop: 16, borderColor: 'var(--border)' }}>
        <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12, color: 'var(--grey-mid)' }}>
            {job.archived
              ? 'This job is archived — it is hidden from the jobs board but nothing has been deleted.'
              : 'Archiving hides the job from the board but keeps every record. Deleting is permanent.'}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline btn-sm" onClick={toggleArchive}>{job.archived ? 'Unarchive' : 'Archive job'}</button>
            <button className="btn btn-danger btn-sm" onClick={deleteJob}>Delete permanently</button>
          </div>
        </div>
      </div>

      {/* Schedule item modal */}
      {schedModal && (
        <div className="modal-overlay" onClick={() => setSchedModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {schedModal === 'new' ? 'Add Schedule Item' : 'Edit Schedule Item'} — {schedTab === 'client' ? 'Client-Facing' : 'Internal'}
              </h3>
              <button className="modal-close" onClick={() => setSchedModal(null)}>×</button>
            </div>
            <form onSubmit={saveSchedItem}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Phase / Task *</label>
                  <input className="form-control" value={schedForm.title} onChange={e => setSchedForm({ ...schedForm, title: e.target.value })} required placeholder="e.g. Groundworks & foundations" />
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <input type="checkbox" checked={schedForm.date_unknown} onChange={e => setSchedForm({ ...schedForm, date_unknown: e.target.checked })} style={{ width: 16, height: 16, accentColor: 'var(--gold)' }} />
                    <span style={{ fontSize: 14, color: 'var(--navy)' }}>Dates not known yet</span>
                  </label>
                  <p style={{ fontSize: 11, color: 'var(--grey-mid)', marginTop: 4, marginLeft: 26 }}>The item shows as "Dates to be confirmed" until you add them.</p>
                </div>
                {!schedForm.date_unknown && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div className="form-group">
                      <label className="form-label">Start Date</label>
                      <input type="date" className="form-control" value={schedForm.start_date} onChange={e => setSchedForm({ ...schedForm, start_date: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">End Date</label>
                      <input type="date" className="form-control" value={schedForm.end_date} onChange={e => setSchedForm({ ...schedForm, end_date: e.target.value })} />
                    </div>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-control" value={schedForm.status} onChange={e => setSchedForm({ ...schedForm, status: e.target.value })}>
                      <option value="upcoming">Upcoming</option>
                      <option value="in_progress">In Progress</option>
                      <option value="complete">Complete</option>
                      <option value="delayed">Delayed</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Whose Responsibility?</label>
                    <select className="form-control" value={schedForm.responsible_user_id} onChange={e => setSchedForm({ ...schedForm, responsible_user_id: e.target.value })}>
                      <option value="">No one assigned</option>
                      {staffList.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                    <p style={{ fontSize: 11, color: 'var(--grey-mid)', marginTop: 4 }}>They get an email when you save.</p>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Details (optional)</label>
                  <textarea className="form-control" rows={3} value={schedForm.details} onChange={e => setSchedForm({ ...schedForm, details: e.target.value })} placeholder={schedTab === 'client' ? 'Written for the customer — keep it plain English.' : 'Internal notes: subcontractors, dependencies, order lead times...'} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setSchedModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-gold" disabled={schedSaving}>{schedSaving ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetail;
